import { useEffect, useState } from "react";
import api, { apiErr, fmtMoney } from "@/lib/api";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, HandCoins, Trash2 } from "lucide-react";
import { toast } from "sonner";

const TYPE_CLASS = { Prime: "bg-emerald-50 text-emerald-700 border-emerald-200", Retenue: "bg-rose-50 text-rose-700 border-rose-200", Avance: "bg-amber-50 text-amber-700 border-amber-200" };

export default function PayrollElements() {
  const [items, setItems] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);

  const load = () => api.get("/payroll/elements").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {}); }, []);

  const openNew = () => { setForm({ employe_id: "", type: "Prime", libelle: "", montant: 0, periode: new Date().toISOString().slice(0, 7), notes: "" }); setOpen(true); };
  const save = async (e) => { e.preventDefault(); try { await api.post("/payroll/elements", form); toast.success("Élément ajouté"); setOpen(false); load(); } catch (err) { toast.error(apiErr(err)); } };
  const remove = async (id) => { await api.delete(`/payroll/elements/${id}`); toast.success("Supprimé"); load(); };
  const empName = (e) => `${e.prenom || ""} ${e.nom || ""}`.trim();

  const list = items || [];
  return (
    <div>
      <PageHeader title="Éléments de paie" subtitle="Primes, retenues et avances par collaborateur & période">
        <Button onClick={openNew} data-testid="add-element" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouvel élément</Button>
      </PageHeader>

      {items === null ? <Loader /> : list.length === 0 ? (
        <EmptyState icon={HandCoins} title="Aucun élément" subtitle="Ajoutez primes, retenues ou avances : ils seront repris automatiquement dans les bulletins de la période." />
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Collaborateur</TableHead><TableHead>Type</TableHead><TableHead>Libellé</TableHead><TableHead>Période</TableHead><TableHead className="text-right">Montant</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {list.map((el) => (
                <TableRow key={el.id} className="hover:bg-slate-50" data-testid={`element-row-${el.id}`}>
                  <TableCell className="font-semibold">{el.employe_name}</TableCell>
                  <TableCell><span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${TYPE_CLASS[el.type]}`}>{el.type}</span></TableCell>
                  <TableCell>{el.libelle}</TableCell>
                  <TableCell className="text-sm font-mono">{el.periode}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-bold">{fmtMoney(el.montant)}</TableCell>
                  <TableCell><Button variant="ghost" size="icon" onClick={() => remove(el.id)}><Trash2 className="h-4 w-4 text-slate-300 hover:text-rose-500" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div></div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-head">Nouvel élément de paie</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={save} className="space-y-3">
              <div><Label className="text-xs">Collaborateur *</Label>
                <Select value={form.employe_id} onValueChange={(v) => setForm({ ...form, employe_id: v })}><SelectTrigger className="mt-1" data-testid="element-employe"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                  <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{empName(e)} · {e.matricule}</SelectItem>)}</SelectContent></Select></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}><SelectTrigger className="mt-1" data-testid="element-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{["Prime", "Retenue", "Avance"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="text-xs">Période</Label><Input type="month" value={form.periode} onChange={(e) => setForm({ ...form, periode: e.target.value })} className="mt-1" data-testid="element-periode" /></div>
              </div>
              <div><Label className="text-xs">Libellé *</Label><Input value={form.libelle} onChange={(e) => setForm({ ...form, libelle: e.target.value })} required className="mt-1" data-testid="element-libelle" /></div>
              <div><Label className="text-xs">Montant (FCFA) *</Label><Input type="number" value={form.montant} onChange={(e) => setForm({ ...form, montant: Number(e.target.value) })} required className="mt-1" data-testid="element-montant" /></div>
              <DialogFooter><Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button><Button type="submit" data-testid="element-submit" className="bg-brand-600 hover:bg-brand-700 text-white">Ajouter</Button></DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
